import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/auth.service';
import { API_ENDPOINTS } from '../config/api';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  BookOpen,
  Users,
  Calendar,
  BarChart3,
  CreditCard,
  User,
  LogOut,
  Menu,
  X,
  Crown,
  Building2,
  HomeIcon
} from 'lucide-react';

interface TrainingCenterInfo {
  id: number;
  name: string;
  logo?: string;
  phone_number: string;
  about: string;
  address: string;
  website: string;
  social_media_links: string;
  field_ids: number[];
  user_id: number;
  wilaya_id?: number;
}

const TrainingCenterLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Start closed on mobile
  const [trainingCenter, setTrainingCenter] = useState<TrainingCenterInfo | null>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    const loadTrainingCenterInfo = async () => {
      try {
        const centerInfo = await authService.getTrainingCenterInfo();
        setTrainingCenter(centerInfo);
      } catch (error) {
      }
    };

    loadTrainingCenterInfo();
  }, []);

  const navigationItems = [
    { icon: LayoutDashboard, label: t('trainingCenter.navigation.dashboard'), path: '/training-center/dashboard' },
    { icon: HomeIcon, label: t('trainingCenter.navigation.goback'), path: '/' },
    { icon: BookOpen, label: t('trainingCenter.navigation.trainings'), path: '/training-center/trainings' },
    { icon: Crown, label: t('trainingCenter.navigation.subscriptions'), path: '/training-center/subscriptions' },
    { icon: Users, label: t('trainingCenter.navigation.participants'), path: '/training-center/participants' },
    { icon: Calendar, label: t('trainingCenter.navigation.calendar'), path: '/training-center/calendar' },
    { icon: BarChart3, label: t('trainingCenter.navigation.statistics'), path: '/training-center/stats' },
    { icon: CreditCard, label: t('trainingCenter.navigation.invoices'), path: '/training-center/invoices' },
    { icon: User, label: t('trainingCenter.navigation.profile'), path: '/training-center/profile' },
  ];

  const isActivePath = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-30 backdrop-blur-sm transition-opacity duration-300 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
          aria-label="Fermer le menu"
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed z-30 inset-y-0 left-0 bg-white shadow-2xl max-h-screen w-64 lg:w-64 transition-all duration-300 border-r border-gray-200 flex flex-col ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {/* Header Sidebar */}
        <div className="flex flex-col items-center py-6 border-b border-gray-200 bg-white">
          <div className="flex items-center space-x-3 mb-3">
            {trainingCenter?.logo ? (
              <img
                src={`${API_ENDPOINTS.UPLOADS}/${trainingCenter.logo}`}
                alt={`${trainingCenter.name} Logo`}
                className="h-12 w-12 rounded-full object-cover border-2 border-brand-primary/20 shadow-md"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <div className={`h-12 w-12 rounded-full bg-gradient-to-br from-brand-primary to-purple-600 flex items-center justify-center shadow-md ${trainingCenter?.logo ? 'hidden' : ''}`}>
              <Building2 className="h-6 w-6 text-white" />
            </div>
          </div>
          <span className="text-lg font-bold text-gray-900 text-center px-2">
            {trainingCenter?.name || 'Centre de Formation'}
          </span>
          <p className="text-xs text-gray-500 mt-1">Tableau de bord</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.path);
            
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  // Close sidebar on mobile after navigation
                  if (window.innerWidth < 1024) {
                    setIsSidebarOpen(false);
                  }
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-200 ${
                  isActive
                    ? 'bg-brand-primary/10 text-brand-primary border border-brand-primary/20'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-brand-primary'
                }`}
              >
                <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${isActive ? 'text-brand-primary' : 'text-gray-500'}`} />
                <span className="font-medium text-sm sm:text-base">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div className="p-3 border-t border-gray-200 bg-white">
          <div className="flex items-center gap-3 mb-3 p-2 bg-gray-50 rounded-lg">
            <div className="h-8 w-8 sm:h-10 sm:w-10 bg-brand-primary/10 rounded-full flex items-center justify-center">
              <User className="h-4 w-4 sm:h-5 sm:w-5 text-brand-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                {user?.fullName || 'Centre de Formation'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email || 'admin@centre.com'}
              </p>
            </div>
          </div>
          
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs sm:text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
          >
            <LogOut className="h-3 w-3 sm:h-4 sm:w-4" />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Hamburger button - Mobile only */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="fixed top-4 left-4 z-50 p-2 text-gray-500 hover:text-gray-600 focus:outline-none rounded-lg border border-transparent hover:border-gray-200 bg-white/90 shadow-md transition-all lg:hidden"
        aria-label={isSidebarOpen ? 'Cacher le menu' : 'Afficher le menu'}
      >
        {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-64">
        {/* Top Navbar */}
        <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6">
            <div className="flex items-center gap-4">
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                {navigationItems.find(item => isActivePath(item.path))?.label || 'Centre de Formation'}
              </h1>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="hidden lg:flex items-center gap-2 text-sm text-gray-600">
                <span>{trainingCenter?.name || 'Centre de Formation'}</span>
                <span>•</span>
                <span>{user?.fullName || 'Admin'}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-4 sm:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default TrainingCenterLayout;
